export type Market = 'moneyline' | 'spread' | 'total'
export type Side = 'home' | 'away' | 'draw' | 'over' | 'under'
/** The order every display lists sides in: away before home, draw last. */
export const sideOrder: Side[] = ['away', 'over', 'home', 'under', 'draw']
export type View = 'win' | 'price'
export type Mode = 'ratio' | 'money' | 'momentum'
export type WindowSize = '1H' | '1D' | '1W' | 'ALL'
export type PricePoint = [
  time: number,
  oddsMilli: number,
  lineMilli: number | null,
  status: 'OPEN' | 'SUSPENDED' | 'CLOSED',
]
export type ScorePoint = [
  time: number,
  home: string,
  away: string,
  clockSeconds: number | null,
  period: string | null,
]
export interface PriceSeries {
  market: Market
  side: Side
  line_kind: string
  points: PricePoint[]
}
export interface HistoryData {
  event_id: string
  offering: string
  source_offering: string
  updated_at: string | null
  series: PriceSeries[]
  scores: ScorePoint[]
}
export interface MarketSettings {
  line_history: {
    enabled: boolean
    views: View[]
    default_view: View
    markets?: Market[]
    default_market?: Market
  }
  action_split: { enabled: boolean; modes: Mode[]; default_mode: Mode; hide_when_thin?: boolean }
  /** Feed cards show the widgets only when on; absent (older APIs) means off. */
  feed_cards?: { enabled: boolean }
  player_form?: { enabled: boolean }
  display?: { odds_format?: 'american' | 'decimal'; start_collapsed?: boolean }
  labels?: {
    line_history_title?: string
    action_split_title?: string
    bettors?: string
    money?: string
  }
  design?: { header?: 'filled' | 'plain'; colors?: 'theme' | 'custom'; side_colors?: string[] }
}
export interface SplitSide {
  outcome_id: string
  bettor_share?: number
  money_share?: number
  amount?: number
  bets?: number
}
export interface MomentumBucket {
  from_ms: number
  to_ms: number
  shares: { outcome_id: string; bet_share: number }[]
}
export type SplitData =
  | { state: 'thin' }
  | { state: 'unavailable' }
  | {
      state: 'ready'
      currency?: string
      outcomes: SplitSide[]
      momentum?: MomentumBucket[]
      updated_at?: string
    }
export interface EventInfo {
  id: string
  home: string
  away: string
  kickoff?: number
  live?: boolean
  name?: string
}
export interface Outcome {
  id: string | number
  odds?: number
  oddsAmerican?: string | number
  line?: number
  status?: string
  type?: string
  label?: string
  participant?: string
  participantId?: string | number
  eventParticipantId?: string | number
}
export interface Offer {
  id: string | number
  eventId?: string | number
  criterion?: { label?: string; englishLabel?: string; lifetime?: string; id?: number }
  betOfferType?: { id?: number; name?: string }
  outcomes?: Outcome[]
  suspended?: boolean
  main?: boolean
  tags?: string[]
}
export interface OfferingData {
  betOffers?: Offer[]
  events?: {
    id: string | number
    homeName?: string
    awayName?: string
    name?: string
    start?: string
    state?: string
    sport?: string
    group?: string
    path?: { name?: string; englishName?: string }[]
    participants?: { participantId: string | number; name?: string }[]
  }[]
}

export const disabledSettings: MarketSettings = {
  line_history: {
    enabled: false,
    views: ['win', 'price'],
    default_view: 'win',
    markets: ['moneyline', 'spread', 'total'],
    default_market: 'moneyline',
  },
  action_split: { enabled: false, modes: ['ratio'], default_mode: 'ratio', hide_when_thin: false },
  feed_cards: { enabled: false },
  player_form: { enabled: false },
  display: { odds_format: 'american', start_collapsed: false },
  labels: { line_history_title: '', action_split_title: '', bettors: '', money: '' },
  design: { header: 'filled', colors: 'theme', side_colors: [] },
}
